import React, { useEffect, useState } from "react";
import { Col, Modal, Row, Table } from "react-bootstrap";
import { api } from "../services/api";
import PageHeader from "./ui/PageHeader";
import DataCard from "./ui/DataCard";
import StatusBadge from "./ui/StatusBadge";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState("");
  const [generating, setGenerating] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [inv, txn] = await Promise.all([api.getInvoices(), api.getTransactions()]);
      setInvoices(inv);
      setTransactions(txn.filter((t) => ["success", "completed"].includes(t.statusKey)));
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const txn = transactions.find((t) => t.id === selectedTxn);
    if (!txn) return;
    setGenerating(true);
    try {
      await api.generateInvoice(txn.transactionId);
      setShowGenerate(false);
      setFeedback({ type: "success", message: "Invoice generated successfully." });
      loadData();
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const filtered = invoices.filter(
    (inv) =>
      inv.invoiceNumber.toLowerCase().includes(filter.toLowerCase()) ||
      inv.customerName.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) return <LoadingState message="Loading invoices..." />;

  return (
    <div>
      <PageHeader
        title="Invoices & Receipts"
        subtitle="GST-compliant payment receipts for Indian customers"
        action={
          <button className="btn-billr btn-billr-primary" onClick={() => setShowGenerate(true)}>
            + Generate Invoice
          </button>
        }
      />

      {feedback && <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />}

      <Row className="mb-4">
        <Col md={4}>
          <input className="billr-input" placeholder="Search invoices..." value={filter} onChange={(e) => setFilter(e.target.value)} />
        </Col>
      </Row>

      <DataCard title={`Invoices (${filtered.length})`}>
        <Table responsive className="billr-table mb-0">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Bill Type</th>
              <th>Amount</th>
              <th>Payment</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inv) => (
              <tr key={inv.invoiceId}>
                <td><code>{inv.invoiceNumber}</code></td>
                <td>
                  <strong>{inv.customerName}</strong>
                  <div className="small text-muted">{inv.customerPhone}</div>
                </td>
                <td>{inv.billType}</td>
                <td><strong>{inv.amount}</strong></td>
                <td>{inv.paymentMethod}</td>
                <td><StatusBadge status={inv.statusKey} label={inv.status} /></td>
                <td>
                  <button className="btn-billr btn-billr-outline btn-billr-sm" onClick={() => setViewInvoice(inv)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DataCard>

      <Modal show={showGenerate} onHide={() => setShowGenerate(false)} centered>
        <Modal.Header closeButton><Modal.Title>Generate Invoice</Modal.Title></Modal.Header>
        <Modal.Body>
          <select className="billr-select" value={selectedTxn} onChange={(e) => setSelectedTxn(e.target.value)}>
            <option value="">Select transaction</option>
            {transactions.map((t) => (
              <option key={t.id} value={t.id}>{t.id} — {t.user} — {t.amount}</option>
            ))}
          </select>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-billr btn-billr-outline" onClick={() => setShowGenerate(false)}>Cancel</button>
          <button className="btn-billr btn-billr-primary" disabled={!selectedTxn || generating} onClick={handleGenerate}>
            {generating ? "Generating..." : "Generate"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!viewInvoice} onHide={() => setViewInvoice(null)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Receipt {viewInvoice?.invoiceNumber}</Modal.Title></Modal.Header>
        <Modal.Body>
          {viewInvoice && (
            <div style={{ fontFamily: "inherit" }}>
              <div className="d-flex justify-content-between mb-4">
                <div>
                  <h4 style={{ color: "#6366f1", margin: 0 }}>billr</h4>
                  <small className="text-muted">India Bill Payments</small>
                </div>
                <StatusBadge status={viewInvoice.statusKey} label={viewInvoice.status} />
              </div>
              <Row className="mb-3">
                <Col md={6}><strong>Customer:</strong> {viewInvoice.customerName}<br /><span className="text-muted">{viewInvoice.customerPhone}</span></Col>
                <Col md={6} className="text-md-end"><strong>Date:</strong> {viewInvoice.date}<br /><strong>Ref:</strong> {viewInvoice.beneficiaryRef}</Col>
              </Row>
              <div className="p-3 rounded" style={{ background: "#f8fafc" }}>
                <Row><Col>Bill Type</Col><Col className="text-end">{viewInvoice.billType}</Col></Row>
                <Row><Col>Operator</Col><Col className="text-end">{viewInvoice.operatorName}</Col></Row>
                <hr />
                <Row><Col><strong>Total Paid</strong></Col><Col className="text-end"><strong style={{ fontSize: "1.25rem", color: "#10b981" }}>{viewInvoice.amount}</strong></Col></Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-billr btn-billr-primary" onClick={() => window.print()}>Print Receipt</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Invoices;
